/**
 * Fallback Relationships - Provides fallback relationship logic for undefined paths
 *
 * When a relationship path is not defined in relationshipPaths.ts, this module
 * provides intelligent fallback relationship construction based on entity types.
 *
 * @module queryBuilderCore/relationships/fallbackRelationships
 */

import {
  type QueryFilter,
  type Relationship,
} from '@/sections/insights/queryBuilder/legacy/types/types';

/**
 * Builds fallback relationship when no defined path exists
 *
 * @param currentEntityType - Source entity type
 * @param nextEntityType - Target entity type
 * @param nextNode - Target node structure
 * @param isOrRelationship - Whether this is an OR relationship
 * @returns Fallback relationship or null if no fallback available
 */
export function buildFallbackRelationship(
  currentEntityType: string,
  nextEntityType: string,
  nextNode: {
    labels: string[];
    filters: QueryFilter[];
    relationships: Relationship[];
  },
  isOrRelationship: boolean
): Relationship | null {
  if (currentEntityType === 'Seed') {
    const fallbackLabel =
      nextEntityType === 'Asset'
        ? 'DISCOVERED'
        : nextEntityType === 'ActiveDirectory'
          ? 'DISCOVERED'
          : nextEntityType === 'Technology'
            ? 'DISCOVERED_BY'
            : nextEntityType === 'Vulnerability'
              ? 'HAS_RISK'
              : 'RELATED_TO';

    const seedRelationship: Relationship = {
      label: fallbackLabel,
      target: {
        labels: nextNode.labels,
        filters: nextNode.filters,
        relationships: nextNode.relationships,
      },
      optional: isOrRelationship,
      select_one: true,
    };

    if (fallbackLabel === 'DISCOVERED' && nextEntityType === 'Asset') {
      seedRelationship.length = -1;
    }

    return seedRelationship;
  } else if (nextEntityType === 'Seed') {
    const fallbackLabel =
      currentEntityType === 'Asset'
        ? 'DISCOVERED'
        : currentEntityType === 'ActiveDirectory'
          ? 'DISCOVERED'
          : currentEntityType === 'Technology'
            ? 'DISCOVERED_BY'
            : currentEntityType === 'Vulnerability'
              ? 'HAS_RISK'
              : 'RELATED_TO';

    const seedRelationship: Relationship = {
      label: fallbackLabel,
      source: {
        labels: nextNode.labels,
        filters: nextNode.filters,
        relationships: nextNode.relationships,
      },
      optional: isOrRelationship,
      select_one: true,
    };

    if (fallbackLabel === 'DISCOVERED' && currentEntityType === 'Asset') {
      seedRelationship.length = -1;
    }

    return seedRelationship;
  } else if (
    currentEntityType === 'ActiveDirectory' ||
    nextEntityType === 'ActiveDirectory'
  ) {
    const isForward = currentEntityType === 'ActiveDirectory';
    const fallbackLabel = 'HAS_ATTRIBUTE';

    return {
      label: fallbackLabel,
      ...(isForward
        ? {
            target: {
              labels: nextNode.labels,
              filters: nextNode.filters,
              relationships: nextNode.relationships,
            },
          }
        : {
            source: {
              labels: nextNode.labels,
              filters: nextNode.filters,
              relationships: nextNode.relationships,
            },
          }),
      optional: isOrRelationship,
      select_one: true,
    };
  } else {
    return {
      label: 'RELATED_TO',
      target: {
        labels: nextNode.labels,
        filters: nextNode.filters,
        relationships: nextNode.relationships,
      },
      optional: isOrRelationship,
      select_one: true,
    };
  }
}
